<template>
  <main role="main" class="py-4">
    <div>
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("ipBlockList") }}</h4>
        <div>
          <b-button variant="outline-secondary" class="mr-4" @click="onClickReloadBtn">
            <b-icon icon="arrow-clockwise"></b-icon> {{ $t("reload") }}
          </b-button>
          <b-button variant="outline-primary" v-b-modal:add-ip-policy-modal>
            <b-icon-plus /> {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between mb-3">
        <div>
          <b-form class="d-inline-block" inline @submit.prevent="onFilterFormSubmit">
            <b-form-input class="mr-2" type="search" minlength="2" maxlength="200" v-model="searchText"
                          style="min-width: 300px;" @keyup.enter="onFilterFormSubmit"/>
            <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
          </b-form>
        </div>
        <div class="d-flex flex-row align-items-center">
          <div v-if="totalItems" class="text-right flex-shrink-0 mr-3">{{ $t("resultCount", { count: totalItems }) }}</div>
          <b-pagination class="my-0 mr-3" v-model="currentPage" :total-rows="totalItems" :per-page="itemsPerPage"></b-pagination>
          <b-form-select v-model="itemsPerPage" :options="itemsPerPageSelections"></b-form-select>
        </div>
      </div>

      <b-table ref="table" :items="tableItemProvider" hover primary-key="id" :fields="tableFields" show-empty
               :per-page="itemsPerPage" :current-page="currentPage">
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button variant="danger" @click="onClickDelete(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </div>

    <b-modal id="add-ip-policy-modal" :title="$t('addIpBlock')" hide-footer no-close-on-backdrop>
      <AddIpPolicyModal modal-id="add-ip-policy-modal" @submitted="refreshData"></AddIpPolicyModal>
    </b-modal>
  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Ref, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import * as ipPolicyService from "@/service/ipPolicyService";
  import {BTable, BvTableCtxObject, BvTableProviderPromiseResult} from "bootstrap-vue/src/components/table";
  import {BvTableFieldArray} from "bootstrap-vue";
  import {DateTime} from "luxon";
  import AddIpPolicyModal from "@/components/admin-settings/AddIpPolicyModal.vue";

  @Component({
    components: { AddIpPolicyModal }
  })
  export default class IpPolicy extends BaseComponent {

    searchText = "";

    @Ref("table") table: BTable;
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#",
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "ipAddress",
        label: "IP",
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
      },
      {
        key: "comment",
        label: this.$t("note").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "createdAt",
        label: this.$t("createdDate").toString(),
        thClass: "text-nowrap",
        tdClass: "text-nowrap",
        formatter: it => DateTime.fromISO(it).toFormat("dd/MM/yyyy HH:mm:ss")
      },
      {
        key: "actions",
        label: "",
        thClass: "text-nowrap",
        tdClass: "text-nowrap"
      }
    ];

    itemsPerPageSelections = [      
      { value: 20, text: "20 " + this.$t("itemsPerPage").toString() },
      { value: 50, text: "50 " + this.$t("itemsPerPage").toString() },
      { value: 100, text: "100 " + this.$t("itemsPerPage").toString() },
      { value: 200, text: "200 " + this.$t("itemsPerPage").toString() },
    ];
    itemsPerPage = 20;
    currentPage = 1;
    totalItems = 0;

    mounted() {

    }

    onClickReloadBtn() {
      this.searchText = "";
      this.currentPage = 1;
      this.table.refresh();
    }

    onFilterFormSubmit() {
      this.table.refresh();
    }

    refreshData() {
      this.table.refresh();
    }

    async tableItemProvider(ctx: BvTableCtxObject): Promise<BvTableProviderPromiseResult> {
      const res = await ipPolicyService.getAll({
        page: ctx.currentPage,
        pageSize: this.itemsPerPage,
        q: this.searchText?.trim(),
      });

      this.totalItems = res.total;
      return res.data;
    }

    async onClickDelete(item) {
      const modalMessage = this.$t("deleteIpBlockConfirm", { id: item.id, ip: item.ipAddress }).toString();
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
        centered: true
      });

      if (confirmDelete) {
        try {
          this.showLoading();
          await ipPolicyService.del(item.id);
          this.table.refresh();
          this.toastSuccessDelay(this.$t("deleteIpBlockSuccess").toString());

        } catch (e) {
          this.toastError(e);

        } finally {
          this.hideLoading();
        }
      }
    }

  }
</script>
